import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { NoticeBox } from "@/components/NoticeBox";

const PLAN_LABELS: Record<string, string> = {
  day_pass: "Day Pass",
  monthly: "Monthly Pro",
};

/**
 * Reads ?checkout=success|cancelled (and &plan=...) from the URL after a
 * Stripe redirect, shows an inline confirmation, and polls billing status
 * briefly until the webhook has actually applied Pro access (it can lag the
 * redirect by a second or two). The GA4 "purchase" event is NOT fired here any
 * more: see PurchaseAnalytics, which reads the real amount from Stripe on the
 * server (and ignores test-mode payments).
 */
export function PurchaseConfirmation() {
  const [location] = useLocation();
  const [checkoutState, setCheckoutState] = useState<{ status: string; plan?: string } | null>(null);
  const [cleaned, setCleaned] = useState(false);

  const billingStatus = trpc.billing.status.useQuery(undefined, {
    enabled: checkoutState?.status === "success",
    refetchInterval: query => (query.state.data?.isPro ? false : 1500),
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    if (!status) return;

    setCheckoutState({ status, plan: params.get("plan") ?? undefined });

    if (status === "cancelled") {
      toast.info("Checkout cancelled — no charge was made.");
    }

    if (!cleaned) {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      url.searchParams.delete("plan");
      window.history.replaceState({}, "", url.toString());
      setCleaned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const isConfirmed = billingStatus.data?.isPro;

  if (!checkoutState || checkoutState.status !== "success") return null;

  const planLabel = checkoutState.plan ? PLAN_LABELS[checkoutState.plan] ?? "Pro" : "Pro";
  const until = billingStatus.data?.proAccessUntil ? new Date(billingStatus.data.proAccessUntil) : null;

  return (
    <div className="container pt-6">
      <NoticeBox className="p-4 rounded-xl flex items-start gap-3">
        {isConfirmed ? (
          <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        ) : (
          <Loader2 className="h-5 w-5 text-primary mt-0.5 shrink-0 animate-spin" />
        )}
        <div>
          <p className="font-medium">
            {isConfirmed ? `\uD83C\uDF89 ${planLabel} activated!` : "Payment received — activating your access..."}
          </p>
          <p className="text-sm text-muted-foreground">
            {isConfirmed && until
              ? `You're all set until ${until.toLocaleString()}.`
              : "This usually takes just a few seconds."}
          </p>
        </div>
      </NoticeBox>
    </div>
  );
}
